import { useState, useEffect } from 'react';
import EntityLink from '../components/EntityLink';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import DocumentWallet from '../components/DocumentWallet';
import AuditLog from '../components/AuditLog';

const TABS = ['Profile', 'Work Orders', 'AP Invoices', 'Ledger', 'Documents'];

function fmt(n) {
  return '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function VendorProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState('Profile');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  function load() {
    setError(false);
    api.get(`/vendors/${id}`)
      .then(r => { setData(r.data); setForm(r.data); })
      .catch(() => setError(true));
  }
  useEffect(() => { load(); }, [id]);

  function sf(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    setSaving(true);
    setSaveError('');
    try {
      await api.put(`/vendors/${id}`, form);
      load();
      setEditing(false);
    } catch (e) {
      setSaveError(e.response?.data?.error || 'Could not save changes');
    } finally {
      setSaving(false);
    }
  }

  if (error) {
    return <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />;
  }
  if (!data) return <StateBlock type="loading" />;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>← Back</button>
          <h1 className="page-title">{data.name}</h1>
          <Badge variant={data.is_active ? 'success' : 'neutral'}>{data.is_active ? 'Active' : 'Inactive'}</Badge>
        </div>
      </div>

      <div className="panel-tabs" style={{ marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t} className={'panel-tab' + (tab === t ? ' active' : '')} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Profile' && (
        <ProfileTab
          data={data} editing={editing} form={form} sf={sf}
          onEdit={() => { setEditing(true); setForm(data); setSaveError(''); }}
          onCancel={() => { setEditing(false); setSaveError(''); }}
          onSave={handleSave} saving={saving} saveError={saveError}
        />
      )}

      {tab === 'Work Orders' && <WorkOrdersTab workOrders={data.work_orders} />}

      {tab === 'AP Invoices' && <APInvoicesTab data={data} />}

      {tab === 'Ledger' && <VendorLedgerTab vendorId={data.id} />}

      {tab === 'Documents' && (
        <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
          <DocumentWallet entityType="vendor" entityId={data.id} entityName={data.name} />
        
      <AuditLog tableName="vendors" recordId={id} /></div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
      <div>{value || '—'}</div>
    </div>
  );
}

function ProfileTab({ data, editing, form, sf, onEdit, onCancel, onSave, saving, saveError }) {
  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Vendor Profile</h3>
        <button className="btn btn-secondary" onClick={editing ? onCancel : onEdit}>{editing ? 'Cancel' : 'Edit'}</button>
      </div>

      {saveError && (
        <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
          {saveError}
        </div>
      )}

      {editing ? (
        <>
          <div className="form-grid">
            <div className="form-field"><label>Vendor Name</label><input value={form.name || ''} onChange={e => sf('name', e.target.value)} /></div>
            <div className="form-field"><label>Contact Name</label><input value={form.contact_name || ''} onChange={e => sf('contact_name', e.target.value)} /></div>
            <div className="form-field"><label>Email</label><input type="email" value={form.email || ''} onChange={e => sf('email', e.target.value)} /></div>
            <div className="form-field"><label>Phone</label><input value={form.phone || ''} onChange={e => sf('phone', e.target.value)} /></div>
            <div className="form-field"><label>Tax ID</label><input value={form.tax_id || ''} onChange={e => sf('tax_id', e.target.value)} /></div>
            <div className="form-field">
              <label>Status</label>
              <select value={form.is_active ? '1' : '0'} onChange={e => sf('is_active', e.target.value === '1' ? 1 : 0)}>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>
          <div className="form-field span-2" style={{ marginTop: 14 }}><label>Address</label><textarea rows={2} value={form.address || ''} onChange={e => sf('address', e.target.value)} /></div>

          <h4 style={{ marginTop: 20, marginBottom: 10 }}>Bank Info</h4>
          <div className="form-grid">
            <div className="form-field"><label>Bank Name</label><input value={form.bank_name || ''} onChange={e => sf('bank_name', e.target.value)} /></div>
            <div className="form-field"><label>Routing Number</label><input value={form.routing_number || ''} onChange={e => sf('routing_number', e.target.value)} /></div>
            <div className="form-field"><label>Account Number</label><input value={form.account_number || ''} onChange={e => sf('account_number', e.target.value)} /></div>
            <div className="form-field">
              <label>Account Type</label>
              <select value={form.account_type || 'Checking'} onChange={e => sf('account_type', e.target.value)}>
                <option value="Checking">Checking</option>
                <option value="Savings">Savings</option>
              </select>
            </div>
          </div>

          <div className="form-field span-2" style={{ marginTop: 14 }}><label>Notes</label><textarea rows={3} value={form.notes || ''} onChange={e => sf('notes', e.target.value)} /></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button className="btn btn-primary" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <Field label="Contact Name" value={data.contact_name} />
            <Field label="Email" value={data.email} />
            <Field label="Phone" value={data.phone} />
            <Field label="Tax ID" value={data.tax_id} />
            <div style={{ gridColumn: '1 / -1' }}><Field label="Address" value={data.address} /></div>
          </div>
          <h4 style={{ marginTop: 20, marginBottom: 10 }}>Bank Info</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <Field label="Bank Name" value={data.bank_name} />
            <Field label="Routing Number" value={data.routing_number} />
            <Field label="Account Number" value={data.account_number} />
            <Field label="Account Type" value={data.account_type} />
          </div>
          {data.notes && <div style={{ marginTop: 16 }}><Field label="Notes" value={data.notes} /></div>}
        </>
      )}
    </div>
  );
}

function WorkOrdersTab({ workOrders }) {
  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <h3 style={{ marginTop: 0 }}>Work Orders / Contractors Placed ({(workOrders || []).length})</h3>
      {(!workOrders || workOrders.length === 0) ? (
        <StateBlock type="empty" title="No work orders yet" />
      ) : workOrders.map(wo => (
        <div key={wo.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 600 }}>{wo.client_wo_number}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            {(wo.engagement_type || '').replace('_', ' ')} · {wo.first_name} {wo.last_name} · {wo.client_name} · <Badge variant={wo.status === 'active' ? 'success' : 'neutral'}>{wo.status}</Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

function APInvoicesTab({ data }) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Total Billed</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.summary.total_billed)}</div>
        </div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Verified Paid</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.summary.total_verified_paid)}</div>
        </div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Outstanding</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.summary.outstanding)}</div>
        </div>
      </div>

      <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
        <h3 style={{ marginTop: 0 }}>AP Invoices ({data.ap_invoices.length})</h3>
        <table className="data-table">
          <thead><tr><th>Invoice #</th><th>Contractor</th><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'right' }}>Verified Paid</th><th>Status</th></tr></thead>
          <tbody>
            {data.ap_invoices.length === 0 && <tr><td colSpan={6}><StateBlock type="empty" title="No AP invoices" /></td></tr>}
            {data.ap_invoices.map(inv => (
              <tr key={inv.id}>
                <td><EntityLink type="ap_invoice" id={inv.id}>{inv.invoice_number}</EntityLink></td>
                <td>{inv.first_name} {inv.last_name}</td>
                <td>{inv.invoice_date}</td>
                <td style={{ textAlign: 'right' }}>{fmt(inv.amount_to_pay)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(inv.verified_paid)}</td>
                <td><Badge>{inv.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function VendorLedgerTab({ vendorId }) {
  const [ledger, setLedger] = useState(null);
  useEffect(() => {
    setLedger(null);
    api.get(`/vendors/${vendorId}/ledger`).then(r => setLedger(r.data));
  }, [vendorId]);

  if (!ledger) return <StateBlock type="loading" />;
  if (ledger.rows.length === 0) return <StateBlock type="empty" title="No ledger activity yet" />;

  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Ledger Balance</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(ledger.summary.ledger_balance)}</div>
        </div>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Verified Balance</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>{fmt(ledger.summary.verified_balance)}</div>
        </div>
      </div>
      <table className="data-table">
        <thead><tr><th>Invoice #</th><th style={{ textAlign: 'right' }}>Amount</th><th>Paid</th><th>Status</th></tr></thead>
        <tbody>
          {ledger.rows.map((r, i) => (
            <tr key={i}>
              <td><EntityLink type="ap_invoice" id={r.invoice_id}>{r.invoice_number}</EntityLink></td>
              <td style={{ textAlign: 'right' }}>{fmt(r.amount)}</td>
              <td>{r.payment_date || '—'}</td>
              <td>{r.verification_status ? <Badge variant={r.verification_status === 'verified' ? 'success' : 'warning'}>{r.verification_status}</Badge> : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
